import {
  Col,
  Empty,
  Form,
  InputNumber,
  Radio,
  RadioChangeEvent,
  Row,
  Select,
  Slider,
  Tooltip,
} from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import RadioButtonRankingOptions from "../radio-button-ranking-options/RadioButtonRankingOptions";
import { RADAR_ROLE_RANKING_INDEX } from "shared/constants/LocalStorageConsts";
import { useDispatch, useSelector } from "react-redux";
import {
  RolesRankingIndex,
  clearRolesRanking,
  getRoleRanking,
} from "explorer/reducers/rankings";
import Table, { ColumnsType } from "antd/lib/table";
import { I18NColumn } from "../I18nColumn";
import { getI18nCoutry } from "shared/helpers/i18nCountry";
import { getI18nGenre } from "shared/helpers/i18nGenre";
import { FieldSelect } from "shared/components";
import {
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { filterOptionDiacritics } from "shared/components/single-multiselect/SingleMultiselect";
interface Props {}

enum TimeWindow {
  OneYear = "1y",
  TwoYears = "2y",
  ThreeYears = "3y",
  FourYears = "4y",
  FiveYears = "5y",
}

const defaultPhonogramCntWindowMin = (time: TimeWindow) => {
  const timeWindowMap: { [key in TimeWindow]: number } = {
    [TimeWindow.OneYear]: 10,
    [TimeWindow.TwoYears]: 20,
    [TimeWindow.ThreeYears]: 30,
    [TimeWindow.FourYears]: 40,
    [TimeWindow.FiveYears]: 50,
  };

  return timeWindowMap[time] || 0;
};

enum Role {
  Writers = "writers",
  // Performers = "performers",
  Producers = "producers",
}

const defaults: any = {
  defaultForm: {
    popularity: [1, 100],
    concentration: [1, 100],
  },
  defaultFilter: {
    role: "",
    timeWindow: "",
    genre: "",
    offset: 0,
    limit: 10,
  },
};

const initial = () => {
  return {
    ...defaults,
  };
};

const RolesRanking: React.FC<Props> = () => {
  const { defaultFilter } = initial();
  const { t, i18n } = useTranslation("radar");
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [filter, setFilter] = useState<any>();
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(TimeWindow.OneYear);
  const [rankingIndex, setRankingIndex] = useState<RolesRankingIndex>(
    RolesRankingIndex.Hitmaker
  );
  const { genres, genreOptions } = useSelector((state: any) => state.genres);
  const { countries, countryOptions } = useSelector(
    (state: any) => state.countries
  );
  const filtereGenreOptions = useMemo(() => {
    return genreOptions.filter((genre: any) => !genre.value.includes("outro"));
  }, [genreOptions]);

  const roleOptions = useMemo(() => {
    return Object.values(Role).map((role) => ({
      label: t(`pages.RolesRanking.${role}`),
      value: role,
    }));
  }, [t]);

  const { rolesRanking, loading } = useSelector((state: any) => state.rankings);

  const { defaultForm } = initial();

  const colunas: ColumnsType<any> = [
    {
      dataIndex: "position",
      render: (value) => <>{value ? value : "N/A"}</>,
      width: "5%",
    },
    {
      title: t("pages.RolesRanking.artistName"),
      dataIndex: "artistName",
      width: "15%",
    },
    {
      title: t("pages.RolesRanking.popularityIndex"),
      dataIndex: "popularityIndex",
      width: "15%",
      align: "center",
    },
    {
      title: t("pages.RolesRanking.concentrationIndex"),
      dataIndex: "concentrationIndex",
      width: "15%",
      align: "center",
    },
    {
      title: t("pages.RolesRanking.genreStrm"),
      dataIndex: "genreStrm",
      width: "15%",
      align: "center",
      render: (value) => (
        <I18NColumn
          listRow={value ? [value] : []}
          label={t("pages.ArtistsRanking.genres")}
          i18nFunction={(genre: string) => getI18nGenre(genre, i18n, genres)}
          t={t}
        />
      ),
    },
    {
      title: t("pages.RolesRanking.mainCountry"),
      dataIndex: "mainCountry",
      width: "15%",
      align: "center",
      render: (value) => (
        <I18NColumn
          listRow={value ? [value] : []}
          label={t("pages.ArtistsRanking.country")}
          i18nFunction={(country: string) =>
            getI18nCoutry(country, i18n, countries)
          }
          t={t}
        />
      ),
    },
    {
      title: t("pages.RolesRanking.phonogramCntWindow"),
      dataIndex: "phonogramCntWindow",
      width: "15%",
      align: "center",
    },
  ];

  const handlePreviousPage = () => {
    if (filter.offset === 0) return;
    setFilter((prevFilter: any) => ({
      ...prevFilter,
      offset: prevFilter.offset - prevFilter.limit,
    }));
  };

  const handleNextPage = () => {
    setFilter((prevFilter: any) => ({
      ...prevFilter,
      offset: prevFilter.offset + prevFilter.limit,
    }));
  };

  const onFinishFilter = () => {
    const { popularity, concentration, ...rest } = form.getFieldsValue(true);
    const [popularityIndexMin, popularityIndexMax] = popularity;
    const [concentrationIndexMin, concentrationIndexMax] = concentration;
    setFilter({
      ...defaultFilter,
      ...rest,
      popularityIndexMin,
      popularityIndexMax,
      concentrationIndexMin,
      concentrationIndexMax,
    });
  };

  const onDeleteClick = () => {
    dispatch(clearRolesRanking());
    setTimeWindow(TimeWindow.OneYear);
    setRankingIndex(RolesRankingIndex.Hitmaker);
    form.resetFields();
    setFilter(null);
  };

  const onChangeIndex = (evt: any) => {
    const saveRankingIndex = evt.target.value;
    if (saveRankingIndex === RolesRankingIndex.TrendMax) {
      setTimeWindow(TimeWindow.FiveYears);
    }
    localStorage.setItem(RADAR_ROLE_RANKING_INDEX, saveRankingIndex);
    setRankingIndex(saveRankingIndex);
    setFilter((prevFilter: any) => ({
      ...prevFilter,
      offset: 0,
    }));
  };

  const onChangeRangeFilter = (evt: RadioChangeEvent) => {
    setTimeWindow(evt.target.value);
    setFilter((prevFilter: any) => ({
      ...prevFilter,
      offset: 0,
    }));
  };

  useEffect(() => {
    const combineFilter = {
      ...filter,
      timeWindow,
      phonogramCntWindowMin:
        filter?.phonogramCntWindowMin ??
        defaultPhonogramCntWindowMin(timeWindow),
    };
    dispatch(
      getRoleRanking({
        filter: combineFilter,
        rankingIndex,
      })
    );
  }, [rankingIndex, filter, timeWindow, dispatch]);

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.Ranking.S4L_RANKING_COMPOSER_PRODUCER,
    });
  }, []);

  return (
    <div className="radar-home">
      <Row style={{ marginTop: 16 }}>
        <Col style={{ textAlign: "center" }}>
          <h1 className="ranking-title">{t("pages.ArtistsRanking.title")}</h1>
        </Col>
      </Row>
      <Row style={{ marginTop: 16 }}>
        <Col style={{ textAlign: "center" }}>
          <RadioButtonRankingOptions value="role" />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} className="conteudo-home">
          <Form
            form={form}
            initialValues={defaultForm}
            onFinish={onFinishFilter}
            className="filtro"
            layout="vertical"
          >
            <Row gutter={24}>
              <Col xs={5}>
                <Form.Item
                  name="genre"
                  label={t("pages.ArtistsRanking.genre")}
                  rules={[
                    {
                      required: true,
                      message: t("pages.RolesRanking.genreRequired"),
                    },
                  ]}
                >
                  <Select
                    showSearch
                    options={filtereGenreOptions}
                    filterOption={filterOptionDiacritics}
                    placeholder={t("pages.ArtistsRanking.selectHere")}
                  />
                </Form.Item>
              </Col>
              <Col xs={5}>
                <Form.Item
                  name="role"
                  label={t("pages.RolesRanking.role")}
                  rules={[
                    {
                      required: true,
                      message: t("pages.RolesRanking.roleRequired"),
                    },
                  ]}
                >
                  <Select
                    options={roleOptions}
                    placeholder={t("pages.ArtistsRanking.selectHere")}
                  />
                </Form.Item>
              </Col>
              <Col xs={5}>
                <Form.Item
                  name="country"
                  label={t("pages.ArtistsRanking.country")}
                >
                  <FieldSelect
                    mode="multiple"
                    showSelectAll={false}
                    optionFilterProp="children"
                    filterOption={(input: string, option: any) =>
                      option?.children
                        ?.toUpperCase()
                        ?.includes(input.toUpperCase())
                    }
                    dataList={countryOptions}
                    placeholder={t("pages.ArtistsRanking.selectHere")}
                  />
                </Form.Item>
              </Col>
              <Col xs={5}>
                <Form.Item
                  name="phonogramCntWindowMin"
                  label={t("pages.RolesRanking.minPhonogramPeriod")}
                >
                  <InputNumber
                    min={0}
                    placeholder={t("pages.ArtistsRanking.typeHere")}
                  />
                </Form.Item>
              </Col>
              <Col xs={4}>
                <div className="filter-button">
                  <span className="delete" onClick={onDeleteClick}>
                    {t("pages.ArtistsRanking.delete")}
                  </span>
                  <button className="apply" disabled={loading}>
                    {t("pages.ArtistsRanking.filter")}
                  </button>
                </div>
              </Col>
              <Col xs={5}>
                <Form.Item
                  name="popularity"
                  label={t("pages.ReleasesRanking.popularity")}
                >
                  <Slider
                    min={1}
                    max={100}
                    range
                    marks={{
                      1: {
                        style: {
                          color: "#fff",
                        },
                        label: <strong>1</strong>,
                      },
                      100: {
                        style: {
                          color: "#fff",
                        },
                        label: <strong>100</strong>,
                      },
                    }}
                    tooltip={{ placement: "bottomLeft" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={5}>
                <Form.Item
                  name="concentration"
                  label={t("pages.RolesRanking.concentration")}
                >
                  <Slider
                    min={1}
                    max={100}
                    range
                    marks={{
                      1: {
                        style: {
                          color: "#fff",
                        },
                        label: <strong>1</strong>,
                      },
                      100: {
                        style: {
                          color: "#fff",
                        },
                        label: <strong>100</strong>,
                      },
                    }}
                    tooltip={{ placement: "bottomLeft" }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      <Row style={{ marginBottom: 16, marginTop: 16 }}>
        <Col
          xs={24}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          <Row justify="center">
            <Radio.Group onChange={onChangeIndex} value={rankingIndex}>
              <Radio.Button value={RolesRankingIndex.Hitmaker}>
                <Tooltip
                  title={t("pages.RolesRanking.hitmakerExplaination")}
                  overlayStyle={{ width: 500 }}
                >
                  {t("pages.RolesRanking.hitmaker")}
                </Tooltip>
              </Radio.Button>
              <Radio.Button value={RolesRankingIndex.Sniper}>
                <Tooltip
                  title={t("pages.RolesRanking.sniperExplaination")}
                  overlayStyle={{ width: 500 }}
                >
                  {t("pages.RolesRanking.sniper")}
                </Tooltip>
              </Radio.Button>
              <Radio.Button value={RolesRankingIndex.Masses}>
                <Tooltip
                  title={t("pages.RolesRanking.massesExplaination")}
                  overlayStyle={{ width: 500 }}
                >
                  {t("pages.RolesRanking.masses")}
                </Tooltip>
              </Radio.Button>
              <Radio.Button value={RolesRankingIndex.Trend}>
                <Tooltip
                  title={t("pages.RolesRanking.trendExplaination")}
                  overlayStyle={{ width: 500 }}
                >
                  {t("pages.RolesRanking.trend")}
                </Tooltip>
              </Radio.Button>
              <Radio.Button value={RolesRankingIndex.TrendMax}>
                <Tooltip
                  title={t("pages.RolesRanking.trendMaxExplaination")}
                  overlayStyle={{ width: 500 }}
                >
                  {t("pages.RolesRanking.trendMax")}
                </Tooltip>
              </Radio.Button>
            </Radio.Group>
          </Row>
          <Row justify="center">
            <Radio.Group
              defaultValue="week"
              value={timeWindow}
              onChange={onChangeRangeFilter}
              style={{ marginBottom: 0 }}
              disabled={rankingIndex === RolesRankingIndex.TrendMax}
            >
              <Radio.Button value={TimeWindow.OneYear}>
                {t("pages.RolesRanking.oneYear")}
              </Radio.Button>
              <Radio.Button value={TimeWindow.TwoYears}>
                {t("pages.RolesRanking.twoYears")}
              </Radio.Button>
              <Radio.Button value={TimeWindow.ThreeYears}>
                {t("pages.RolesRanking.threeYears")}
              </Radio.Button>
              <Radio.Button value={TimeWindow.FourYears}>
                {t("pages.RolesRanking.fourYears")}
              </Radio.Button>
              <Radio.Button value={TimeWindow.FiveYears}>
                {t("pages.RolesRanking.fiveYears")}
              </Radio.Button>
            </Radio.Group>
          </Row>
        </Col>
      </Row>
      {!rolesRanking[rankingIndex] && !loading ? (
        <div>
          <Empty description={t("pages.RolesRanking.noData")} />
        </div>
      ) : (
        <Row style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Table
              columns={colunas}
              dataSource={rolesRanking[rankingIndex] || [{}]}
              loading={loading}
              pagination={false}
              rowKey={(record) =>
                `${record.position}_${record.artistIdSpotify}`
              }
              scroll={{ x: 1000 }}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 16,
                gap: 8,
              }}
            >
              <button
                className="botao-roxo"
                onClick={handlePreviousPage}
                disabled={filter?.offset === 0 || !rolesRanking[rankingIndex]}
              >
                {`<`}
              </button>
              <button
                className="botao-roxo"
                onClick={handleNextPage}
                disabled={
                  !rolesRanking[rankingIndex] ||
                  rolesRanking[rankingIndex].length < 10
                }
              >
                {`>`}
              </button>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default RolesRanking;
